import React from "react";
import { Box } from "@mui/material";
import { MEDIA } from "../../data/media";
import { color, motion, type } from "../../design/tokens";
import Reveal from "./Reveal";

/**
 * An image plate.
 *
 * All photography on the site renders through this, which is what keeps two
 * promises structurally rather than by convention:
 *
 *  • Alt text always comes from the media registry, so it always describes the
 *    real photo. There is no way to pass a photo without one.
 *  • The caption, where shown, is the registry's truthful description of what
 *    is actually in frame. Several of these photos are mid-build rather than
 *    finished rooms, and the caption is what stops that being misleading.
 *
 * The default frame is 4:5 because the source photography is phone-shot 1:1
 * and 4:5. Cropping it to a wide letterbox is what makes contractor sites look
 * like they bought their photos.
 *
 * The wrapper carries `aspect-ratio`, so the box is laid out before the image
 * loads and nothing shifts (CLS stays at zero).
 */
const Figure = ({
  media,
  ratio,
  caption = true,
  captionText,
  priority = false,
  reveal = true,
  tone = "ink",
  objectPosition = "center",
  sx,
  imgSx,
  ...rest
}) => {
  const item = typeof media === "string" ? MEDIA[media] : media;

  if (!item) return null;

  const frameRatio = ratio || item.ratio || "4 / 5";
  const text = captionText === undefined ? item.caption : captionText;

  const frame = (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        aspectRatio: frameRatio,
        overflow: "hidden",
        backgroundColor: tone === "onInk" ? color.inkRaised : color.paperEdge,
        ...sx,
      }}
    >
      <Box
        component="img"
        src={item.src}
        alt={item.alt}
        // The hero image is the LCP element — it must not be deferred, and
        // fetchpriority moves it ahead of the font requests.
        loading={priority ? "eager" : "lazy"}
        decoding={priority ? "sync" : "async"}
        fetchpriority={priority ? "high" : undefined}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition,
          ...imgSx,
        }}
      />
    </Box>
  );

  return (
    <Box component="figure" sx={{ margin: 0, width: "100%" }} {...rest}>
      {reveal && !priority ? (
        <Reveal image>{frame}</Reveal>
      ) : (
        frame
      )}

      {caption && text ? (
        <Box
          component="figcaption"
          sx={{
            ...type.caption,
            color: tone === "onInk" ? color.onInkMute : color.mute,
            marginTop: 1.75,
            paddingLeft: 2,
            borderLeft: `1px solid ${tone === "onInk" ? color.lineOnInk : color.line}`,
            transition: `color ${motion.base} ${motion.ease}`,
          }}
        >
          {text}
        </Box>
      ) : null}
    </Box>
  );
};

export default Figure;
